import API_ENDPOINT from "../config";

/**
 * Guest online payment API.
 *
 * Flip GUEST_PAY_API_READY to true once these endpoints are live:
 *   GET  /guest/invoice/:invoiceNumber
 *   GET  /guest/pay/:invoiceNumber
 *
 * Expected invoice payload:
 * {
 *   status: true,
 *   invoice: {
 *     invoice_number: "0123456789",
 *     amount: 2360,
 *     description: "1 Year Subscription",
 *     plan_name: "1 Year",
 *     status: 0,            // 0 unpaid | 1 paid | 2 rejected
 *     customer_name: "Abebe",
 *     company_name: null,
 *     created_at: "2026-08-01T00:00:00.000000Z"
 *   }
 * }
 *
 * Expected pay payload:
 * { status: true, url: "https://checkout.chapa.co/...", message: "..." }
 */
export const GUEST_PAY_API_READY = true;

export const guestInvoiceUrl = (invoiceNumber) =>
  `${API_ENDPOINT}/guest/invoice/${encodeURIComponent(invoiceNumber)}`;

export const guestPayUrl = (invoiceNumber) =>
  `${API_ENDPOINT}/guest/pay/${encodeURIComponent(invoiceNumber)}`;

export const normalizeGuestInvoice = (payload, fallbackNumber) => {
  const invoice = payload?.invoice || payload || {};
  return {
    invoice_number: invoice.invoice_number || fallbackNumber || "",
    amount: Number(invoice.amount) || 0,
    description: invoice.description || invoice.plan_name || "",
    plan_name: invoice.plan_name || invoice.description || "",
    status: Number(invoice.status),
    customer_name: invoice.customer_name || invoice.name || "",
    company_name: invoice.company_name || "",
    created_at: invoice.created_at || "",
  };
};

export const previewGuestInvoice = (invoiceNumber) => ({
  invoice_number: invoiceNumber,
  amount: 2360,
  description: "1 Year Subscription",
  plan_name: "1 Year",
  status: 0,
  customer_name: "",
  company_name: "",
  created_at: "",
});

export const fetchGuestInvoice = async (invoiceNumber) => {
  const response = await fetch(guestInvoiceUrl(invoiceNumber));
  const payload = await response.json().catch(() => ({}));

  if (response.status === 404 || payload?.status === false) {
    const error = new Error(payload?.message || "Invoice not found");
    error.code = "not_found";
    throw error;
  }

  if (!response.ok) {
    const error = new Error(payload?.message || "Could not load invoice");
    error.code = "load_failed";
    throw error;
  }

  return normalizeGuestInvoice(payload, invoiceNumber);
};

export const createGuestCheckout = async (invoiceNumber) => {
  const response = await fetch(guestPayUrl(invoiceNumber));
  const payload = await response.json().catch(() => ({}));

  if (response.status === 409 || Number(payload?.invoice?.status) === 1) {
    const error = new Error(payload?.message || "Invoice already paid");
    error.code = "already_paid";
    error.invoice = payload?.invoice;
    throw error;
  }

  if (!response.ok || payload?.status === false || !payload?.url) {
    const error = new Error(
      payload?.message || "Could not start payment. Please try again."
    );
    error.code = "pay_failed";
    throw error;
  }

  return payload;
};
